import clsx from "clsx";
import Picture from "@react/widget/Picture";

export const colors = {
  "none": {
    color: "",
    subcolor: "",
    text: "",
  },
	"white": {
    color: "bg-white",
    subcolor: "bg-gray-50",
    text: "text",
  },
	"gray": {
    color: "bg-gray-50",
    subcolor: "bg-white",
    text: "text",
  },
  "gray-dark": {
    color: "bg-gray",
    subcolor: "",
    text: "white",
  },
  "gray-md": {
    color: "bg-[#eeeeef]",
    subcolor: "",
    text: "",
  },
  "gray-ir": {
    color: "bg-gray-ir",
    subcolor: "",
    text: "white",
  }
};

export const textColors = {
  "default": "",
  "white": "white",
  "description": "description",
  "placeholder": "placeholder",
  "disabled": "disabled",
};

export const fits = {
  "contain": "object-contain",
  "cover": "object-cover",
  "fill": "object-fill",
  "none": "object-none",
  "down": "object-scale-down",
};

export const positions = {
  "bottom": "object-bottom",
  "center": "object-[70%] sm:object-[80%] md:object-center",
  "left": "object-left",
  "left-bottom": "object-left-bottom",
  "left-top": "object-left-top",
  "right": "object-right",
  "right-bottom": "object-right-bottom",
  "right-top": "object-right-top",
  "top": "object-top",
};

type Color = keyof typeof colors;
type TextColor = keyof typeof textColors;
type Fit = keyof typeof fits;
type Position = keyof typeof positions;

interface BGColor extends ColorSwitch {
  class: Color;
};

interface TColor extends ColorSwitch {
  class: TextColor
};

interface ImageStyle {
  inner: boolean;
  fit: Fit;
  position: Position;
};

interface BackgroundType {
  __typename: "background_BlockType";
  color: BGColor;
  text: TColor;
  image: Array<Image>;
  imageStyle: Array<ImageStyle>
}

export type BackgroundProps = {
  inner?: boolean;      // is this in the container
  class?: string;       // custom class
  color?: string | Color;     // preset value
  position?: string;
  image?: Image & {
    loading?: "eager" | "lazy";
  };        // image objects
};

export interface BG {
  name: string;
  color: string;
  subcolor: string;
  text: string;
  image?: Image;
  imageStyle: ImageStyle;
  source?: BackgroundType;
  background?: BackgroundProps;
};

export function parserBG(background?:BackgroundType | Array<BackgroundType>) {
  const [bg] = (background instanceof Array) ? background : ((typeof background === 'object') ? [background] : [undefined]);
  var data:BG = {
    name: "none",
    color: colors["none"].color,
    subcolor: colors["none"].subcolor,
    text: colors["none"].text,
    image: undefined,
    imageStyle: {inner: false, fit: "cover", position: "center"}
  };

  if(bg) {
    const color = (bg && Object.keys(colors).includes(bg?.color?.class)) ? colors[bg.color.class] : colors["none"];
    const text = (bg && bg.text && textColors[bg.text.class]) ?? color.text;
    const [image] = bg.image ?? [];
    const [imageStyle] = bg.imageStyle ?? [data.imageStyle];
    data = Object.assign(data, {
      name: bg.color.class,
      color: color.color,
      subcolor: color.subcolor,
      text,
      image,
      imageStyle,
      source: bg
    });
  }

  return Object.assign(data, {
    attrs: {
      inner: data.imageStyle.inner,
      class: undefined,
      color: data.color,
      position: undefined,
      image: data.image ? Object.assign({...data.image}, {
        class: clsx("h-56 sm:h-72 w-full md:h-full", fits[data.imageStyle.fit], positions[data.imageStyle.position])
      }) : undefined,
    } as BackgroundProps
  });
}

interface Props extends Record<string, any> {
  data?: BackgroundProps;
};

const Background:React.FC<Props> = ({ data = {} }) => {
  const { class: className, color = "none", position = "absolute", image } = data;
  return (
    <div className={clsx("background inset-0 z-[-1] flex justify-end", position, color, className)}>
      { !!image && <Picture className={clsx(image.class ?? "h-56 sm:h-72 w-full md:h-full")} src={image} loading={image.loading} sources={[
        {media: "(max-width: 479px)", srcSet: [{width: 465, height: 225, position: "right"}], width: 465, height: 225},
        {media: "(max-width: 767px)", srcSet: [{width: 760, height: 290, position: "right"}], width: 760, height: 290},
        {media: "(max-width: 1439px)", srcSet: [{width: 1440, height: 480, position: "right"}], width: 1440, height: 480},
        {media: "(min-width: 1440px)", srcSet: [{width:2560},{width:1920}]}
      ]}/> }
    </div>
  );
};

export default Background;